import React, { useState } from 'react';
import { useDashboardTranslation } from '../i18n';
import { ChevronDown, X as XIcon, Info, AlertCircle } from 'lucide-react';
import type { SchemaField, FieldMappingOverride, WidgetFieldMapping } from '../types';

interface FieldMappingPanelProps {
  /** Schema del endpoint (fields con labels). Null si no disponible. */
  schema: { fields: SchemaField[] } | null;
  /** Field mapping semántico del widget (fallback cuando no hay schema en cache). */
  widgetFieldMapping?: WidgetFieldMapping | null;
  /** Campos actualmente resueltos (del contrato o heurística). */
  currentCategory: string;
  currentValues: string[];
  /** Override del usuario (si existe). */
  override: FieldMappingOverride | null;
  /** Tipo de chart actual (para adaptar labels). */
  chartType: string;
  /** Callback al cambiar el override. */
  onChange: (override: FieldMappingOverride | null) => void;
}

/**
 * Construye un schema sintético desde field_mapping cuando el endpoint
 * no ha respondido todavía (cache vacío). Permite que el panel funcione
 * sin necesidad de que el widget haya cargado datos.
 */
function buildFallbackSchema(fm: WidgetFieldMapping): { fields: SchemaField[] } {
  // Preferir schema_hints si el widget los tiene pre-declarados en BD
  if (fm.fields?.length) {
    return {
      fields: fm.fields.map(f => ({
        name: f.name,
        type: f.type as SchemaField['type'],
        label: fm.aliases?.[f.name] ?? f.label,
      })),
    };
  }
  // Fallback: construir desde category + values
  const fields: SchemaField[] = [];
  if (fm.category) {
    fields.push({ name: fm.category, type: 'string', label: fm.aliases?.[fm.category] ?? fm.category });
  }
  for (const v of fm.values ?? []) {
    fields.push({ name: v, type: 'number', label: fm.aliases?.[v] ?? v });
  }
  return { fields };
}

import { FIELD_MAPPING_COLORS } from '../utils/chartColors';

/** Busca el label humano de un campo en el schema. Fallback al nombre técnico. */
function getLabel(schema: { fields: SchemaField[] } | null, fieldName: string): string {
  if (!schema) return fieldName;
  const field = schema.fields.find(f => f.name === fieldName);
  return field?.label ?? fieldName;
}

/** Filtra campos válidos para "Agrupar por" (eje X / dimensión): string y date. */
function getCategoryFields(schema: { fields: SchemaField[] } | null): SchemaField[] {
  if (!schema) return [];
  return schema.fields.filter(f => f.type === 'string' || f.type === 'date');
}

/** Filtra campos numéricos del schema (candidatos para values). */
function getNumericFields(schema: { fields: SchemaField[] } | null): SchemaField[] {
  if (!schema) return [];
  return schema.fields.filter(f => f.type === 'number');
}

export const FieldMappingPanel: React.FC<FieldMappingPanelProps> = ({
  schema,
  widgetFieldMapping,
  currentCategory,
  currentValues,
  override,
  chartType,
  onChange,
}) => {
  const { t } = useDashboardTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Usar schema en vivo si existe; si no, construir uno desde field_mapping.
  // Permite que el panel funcione aunque el widget no haya cargado datos todavía.
  const effectiveSchema = schema
    ?? (widgetFieldMapping?.values?.length ? buildFallbackSchema(widgetFieldMapping) : null);

  const activeCategory = override?.category ?? currentCategory;
  const activeValues = override?.values && override.values.length > 0 ? override.values : currentValues;
  const isDonut = chartType === 'chart_donut';
  const maxValues = isDonut ? 1 : 5;
  const hasOverride = override != null;

  const stringFields = getCategoryFields(effectiveSchema);
  const numericFields = getNumericFields(effectiveSchema);
  const availableToAdd = numericFields.filter(f => !activeValues.includes(f.name));

  // Label adaptativo según tipo de chart
  const valuesLabel = chartType === 'chart_line' ? 'Líneas a graficar'
    : chartType === 'chart_donut' ? 'Valor del gráfico'
    : 'Mostrar valores de';

  const handleCategoryChange = (field: string) => {
    onChange({ ...override, category: field, values: override?.values });
  };

  const handleAddValue = (field: string) => {
    const newValues = [...activeValues, field];
    onChange({ ...override, category: override?.category, values: newValues });
  };

  const handleRemoveValue = (field: string) => {
    const newValues = activeValues.filter(v => v !== field);
    if (newValues.length === 0) return; // no permitir vacío
    onChange({ ...override, category: override?.category, values: newValues });
  };

  const handleReset = () => {
    onChange(null);
  };

  // Sin schema ni field_mapping no hay nada que mostrar
  if (!effectiveSchema) return null;

  return (
    <div>
      {/* Toggle sutil */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{t('dashboard.fields.chartFields')}</span>
          {!hasOverride && (
            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">auto</span>
          )}
          {hasOverride && (
            <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">personalizado</span>
          )}
          {!schema && effectiveSchema && (
            <span className="flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200" title="El widget no ha cargado datos todavía. Se muestran los campos configurados en el catálogo.">
              <AlertCircle className="w-2.5 h-2.5" />sin datos
            </span>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Panel expandido */}
      {isOpen && (
        <div className="bg-gray-50 rounded-lg p-3.5 space-y-3.5 mt-1">
          {/* Agrupar por */}
          <div>
            <div className="text-[11px] text-gray-500 mb-1.5">{t('dashboard.fields.groupBy')}</div>
            <select
              value={activeCategory}
              onChange={e => handleCategoryChange(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-md px-2.5 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 truncate"
            >
              {stringFields.length > 0
                ? stringFields.map(f => (
                    <option key={f.name} value={f.name}>
                      {f.label !== f.name ? `${f.label} (${f.name})` : f.name}
                    </option>
                  ))
                : <option value={activeCategory}>{getLabel(effectiveSchema, activeCategory)}</option>
              }
            </select>
          </div>

          {/* Valores seleccionados */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] text-gray-500">{valuesLabel}</div>
              <div className="text-[10px] text-gray-400">{activeValues.length} de {maxValues} permitido{maxValues > 1 ? 's' : ''}</div>
            </div>

            <div className="space-y-1.5">
              {activeValues.map((field, i) => (
                <div key={field} className="bg-white border border-gray-200 rounded-md px-2.5 py-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: FIELD_MAPPING_COLORS[i % FIELD_MAPPING_COLORS.length] }} />
                    <div className="w-4 h-4 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center text-[9px] font-semibold">#</div>
                    <div>
                      <div className="font-medium text-gray-800">{getLabel(effectiveSchema, field)}</div>
                      <div className="text-[10px] text-gray-400">campo: {field}</div>
                    </div>
                  </div>
                  {activeValues.length > 1 && (
                    <button onClick={() => handleRemoveValue(field)} className="text-gray-400 hover:text-red-500 transition-colors">
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}

              {/* Agregar otro valor */}
              {activeValues.length < maxValues && availableToAdd.length > 0 && (
                <div className="relative group">
                  <select
                    value=""
                    onChange={e => { if (e.target.value) handleAddValue(e.target.value); }}
                    className="w-full bg-white border border-dashed border-gray-300 rounded-md px-2.5 py-2 text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                  >
                    <option value="">+ Agregar otro valor</option>
                    {availableToAdd.map(f => (
                      <option key={f.name} value={f.name}>{f.label} ({f.name})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Warning donut */}
          {isDonut && currentValues.length <= 1 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 flex gap-2 text-[11px] text-amber-800 leading-relaxed">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <div>El gráfico circular solo muestra un valor a la vez. Cambia a barras o líneas para comparar varios.</div>
            </div>
          )}

          {/* Campos disponibles del endpoint */}
          <div>
            <div className="text-[11px] text-gray-500 mb-1.5">{t('dashboard.fields.available')}</div>
            <div className="flex flex-wrap gap-1">
              {effectiveSchema.fields.map(f => {
                const isUsed = f.name === activeCategory || activeValues.includes(f.name);
                return (
                  <span
                    key={f.name}
                    className={`text-[10px] px-2 py-0.5 rounded border ${
                      isUsed
                        ? 'bg-blue-50 border-blue-200 text-blue-600'
                        : 'bg-white border-gray-200 text-gray-300 line-through'
                    }`}
                  >
                    {f.name}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Reset */}
          {hasOverride && (
            <button
              onClick={handleReset}
              className="w-full text-center text-[11px] text-gray-500 hover:text-blue-600 py-1 transition-colors"
            >
              Restaurar campos automáticos
            </button>
          )}
        </div>
      )}
    </div>
  );
};
